import React, { memo, useCallback, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import Modal from '../modal'
import ModalBody from '../modalBody'
import FormGroup from '../formGroup'
import FormLabel from '../formLabel'
import FormInput from '../formInput'
import FormInputGroup from '../formInputGroup'
import FormInputAddon from '../formInputAddon'
import Button from '../button'
import { translate } from '../../helpers/stringHelpers'
import Alert from '../alert'
import { formatCurrency } from '../../helpers/currencyHelpers'
import ConfigContext from '../../contexts/configContext'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import FeesAlert from '../feesAlert'
import { toast } from 'react-toastify'
import { trackEvent } from '../../withTracking'
import { listings, listingSales, notifications } from '../../actions'
import trackingEvents from '../../constants/trackingEvents'

const {
  CATEGORY_LISTING,
  ACTION_AUTOBID
} = trackingEvents

export const PurchaseConfirmationModalAutoBid = ({
  isOpen,
  listingUuid,
  onClose
}) => {
  const { locale } = useContext(ConfigContext)

  const [availableAutoBids, setAvailableAutoBids] = useState([])
  const [isAwaitingResponse, setAwaitingResponse] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const dispatch = useDispatch()
  const increments = useSelector(state => state.listingSales.incrementTables[listingUuid], shallowEqual)
  const listing = useSelector(state => state.lots.listings[listingUuid], shallowEqual)
  const { currencyCode, nextBidValue, nextBidCalculation } = useSelector(state => state.listingSales.sales[listingUuid] || {}, shallowEqual)
  const autoBidValue = useSelector(state => state.maxBids.maxBidsByListingUuid[listingUuid]?.value || 0)
  const userUuid = useSelector(state => state.users.user.uuid)
  const userId = useSelector(state => state.users.user.id)
  const isWebcast = listing?.type === 'webcast'
  const auctionUuid = listing?.isMarketplace ? 'marketplace' : listing?.auctionUuid
  const isFavourited = useSelector(state => state.lots.favourites[auctionUuid]?.[listingUuid])
  const registrantId = useSelector(state => (userUuid && state.registrations.registrantIdsByUserUuidByAuction[listing?.auctionUuid]?.[userUuid]?.id))

  const minValue = Math.max(nextBidValue, autoBidValue) || 0
  const incrementCount = increments && Object.keys(increments).length

  const onChange = useCallback(
    (e) => {
      setInputValue(e.target.value)
    },
    []
  )

  useEffect(() => {
    if (isOpen) {
      dispatch(listingSales.getIncrements(listingUuid))
    }
  }, [listingUuid, isOpen, onChange, dispatch])

  useEffect(() => {
    const upcomingAutoBidValues = [minValue]
    if (listingUuid && increments) {
      // get the keys (incrementCaps) for each increment, and sort
      // the "0" key should be used as our final increment, so that should always go to the end
      const keysInOrder = Object.keys(increments).sort((a, b) => (b === '0') ? -1 : parseInt(a) - parseInt(b))
      let lastCalculatedValue = minValue
      // create 100 upcoming autoBid values
      for (let index = 0; index < 100; index++) {
        // set the increment to the final increment value as a fallback
        let increment = increments[0]
        // loop through each "key", in this case each current bid value, to update what we'll be incrementing
        keysInOrder.every(incrementCap => { // eslint-disable-line no-loop-func
          // if the current bid value is less than the incrementCap, set the temporary increment variable
          increment = increments[incrementCap]
          // break out of the loop if we haven't hit the cap
          return lastCalculatedValue >= incrementCap
        })
        // use the final (largest) increment variable to generate a new value
        lastCalculatedValue = lastCalculatedValue + increment
        // push into our autoBidValues array
        upcomingAutoBidValues.push(lastCalculatedValue)
      }
      setAvailableAutoBids(upcomingAutoBidValues)
      if (!inputValue && incrementCount > 1) {
        onChange({
          target: {
            value: `${upcomingAutoBidValues[0]}`
          }
        })
      }
    }
  }, [minValue, listingUuid, incrementCount, increments, inputValue, onChange])

  if (!listing || listing.type === 'listing') return null

  const perUnitText = translate('JspPublicCodes_JSP_LISTING_PRICE_PER_QUANTITY').replace('{0}', '')

  const handleAutoBidConfirm = async (e) => {
    e.preventDefault()
    try {
      setAwaitingResponse(true)
      await dispatch(listingSales.placeAutoBid(listingUuid, listing.id, registrantId, userUuid, userId, inputValue, isWebcast))
      trackEvent(CATEGORY_LISTING, ACTION_AUTOBID, listingUuid, inputValue)
      if (!isFavourited) {
        dispatch(listings.favouriteToggle(auctionUuid, listingUuid, listing.id, userId))
      }
      setInputValue('')
      const successMessage = translate(listing.isMarketplace ? 'JspPublicCodes_JSP_MARKETPLACE_AUTOBID_SUCCESSFUL' : 'JspPublicCodes_JSP_LISTING_AUTOBID_SUCCESSFUL')
        .replace('{0}', formatCurrency(locale, currencyCode, inputValue))
        .replace('{1}', listing.isMarketplace ? listing.id : listing.lotNumber)

      dispatch(notifications.addAuctionNotification(auctionUuid, listingUuid, 'info', successMessage))
    } catch (err) {
      console.error(err)
      toast.error(err)
    }
    onClose()
    setAwaitingResponse()
  }

  return (
    <Modal onClose={onClose} isOpen={isOpen} id='autoBidModal'>
      <ModalBody>
        <h4 className='modal__primary-title' id='autobid-confirm__title'>{translate('JspPublicCodes_JSP_SALE_PLACE_AUTOBID')}</h4>
        <h5 id='autobid-confirm__title--listing'>{listing.title}</h5>
        <form onSubmit={handleAutoBidConfirm}>
          <FormGroup>
            <FormLabel for='autoBid__input' className='sr-only'>{translate('JspPublicCodes_JSP_BIDDING_ENTER_AUTOBID')}</FormLabel>
            {incrementCount > 1
              ? (
                <FormInputGroup>
                  <FormInput
                    min={minValue}
                    onChange={setInputValue}
                    placeholder={translate('JspPublicCodes_JSP_BIDDING_ENTER_AUTOBID')}
                    id='autoBid__input'
                    type='select'
                    value={inputValue}
                  >
                    {availableAutoBids.map(item => (
                      <option key={`${item}`} value={`${item}`}>{formatCurrency(locale, currencyCode, item)}</option>
                    ))}
                  </FormInput>
                  <FormInputAddon>
                    {nextBidCalculation
                      ? perUnitText
                      : null}
                  </FormInputAddon>
                </FormInputGroup>
                )
              : (
                <FormInputGroup>
                  <FormInput
                    min={minValue}
                    onChange={onChange}
                    dataRef='confirmationInput'
                    placeholder={translate('JspPublicCodes_JSP_BIDDING_ENTER_AUTOBID')}
                    id='autoBid__input'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    type='text'
                    value={inputValue}
                  />
                  <FormInputAddon>{currencyCode}{nextBidCalculation ? ` ${perUnitText}` : ''}</FormInputAddon>
                </FormInputGroup>
                )}
          </FormGroup>
          {inputValue && (inputValue < minValue)
            ? (
              <Alert id='autobid-confirm__alert--warning' color='warning'>
                {translate('JspPublicCodes_JSP_LISTING_AUTOBID_MINIMUM_WARNING').replace('{0}', formatCurrency(locale, currencyCode, minValue))}
              </Alert>
              )
            : null}
          {inputValue && minValue && (inputValue >= minValue * 20)
            ? (
              <Alert id='autobid-confirm__alert--danger' color='danger'>
                {translate('JspPublicCodes_JSP_LISTING_AUTOBID_SAFETY_WARNING')}
              </Alert>
              )
            : null}
          <FeesAlert
            listingUuid={listingUuid}
          />
          <Button
            id='autoBid__cancel'
            onClick={onClose}
          >
            {translate('JSTextCodes_CANCEL')}
          </Button>
          <Button
            disabled={!!isAwaitingResponse || inputValue < minValue}
            id='autoBid__submit'
            type='submit'
            color='primary'
            className='pull-right'
          >
            {translate('JspPublicCodes_JSP_SALE_PLACE_AUTOBID')}
          </Button>
        </form>
      </ModalBody>
    </Modal>
  )
}

PurchaseConfirmationModalAutoBid.propTypes = {
  isOpen: PropTypes.bool,
  listingUuid: PropTypes.string,
  onClose: PropTypes.func.isRequired
}

const isEqual = (a, b) => a.isOpen === b.isOpen &&
  a.listingUuid === b.listingUuid

export default memo(PurchaseConfirmationModalAutoBid, isEqual)
